/**
 * AddEditCardV2 — V2 page chrome + form for individual Cards inside
 * a Deck. Same fetch / save graph as v1
 * (Pages/CardDeck/Card/AddEditCard.js).
 *
 *   • GET /decks/cards/{id} via apiV2 → card state
 *   • saveCard(card, onSuccess) → POST/PUT /decks/cards
 *   • closeEditCard(deckId, navigate) on cancel
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';

import LoadingBackdrop from '../../../Common/LoadingBackdrop';
import CardFormFieldsV2 from './v2/CardFormFieldsV2';
import { closeEditCard, saveCard } from '../../../../actions/cardsActions';
import { fetchAllMediaResources } from '../../../../actions/mediaResourceActions';
import { apiV2 } from '../../../../actions';
import { Button, cn } from '../../../redesign';

const SaveIconSvg = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

export default function AddEditCardV2() {
  const { deckId, id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [isLoading, setIsLoading] = useState(false);
  const saveLoading = useSelector((s) => s.cards?.loading);
  const [isModified, setModified] = useState(false);

  const [card, setCard] = useState({
    back_image_url: '',
    front_image_url: '',
    is_active: false,
    linked_content_button_icon_url: '',
    linked_content_button_text: '',
    share_image_url: '',
    share_link_description: '',
    subtitle: '',
    target_android_deeplink_url: '',
    target_deeplink_image_url: '',
    target_ios_deeplink_url: '',
    target_web_deeplink_url: '',
    title: '',
  });

  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    apiV2.get(`/decks/cards/${id}`)
      .then((response) => {
        setModified(false);
        setCard(response.data);
      })
      .catch(() => Swal.fire({ icon: 'error', title: 'Failed to load card' }))
      .finally(() => setIsLoading(false));
  }, [id]);

  useEffect(() => {
    dispatch(fetchAllMediaResources());
  }, [dispatch]);

  const handleChange = (e) => {
    setModified(true);
    setCard((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSave = (e) => {
    if (e?.preventDefault) e.preventDefault();
    const cardToSave = { ...card, deck_id: deckId };
    setModified(false);
    dispatch(saveCard(cardToSave, () => navigate(`/card-decks/${deckId}/cards`)));
  };

  const handleCancel = () => {
    if (!isModified) {
      dispatch(closeEditCard(deckId, () => navigate(`/card-decks/${deckId}/cards`)));
      return;
    }
    Swal.fire({
      icon: 'warning',
      text: 'You have unsaved changes. Are you sure you want to leave?',
      showConfirmButton: true,
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: 'Save & Close',
      cancelButtonText: 'Close Without Saving',
    }).then((result) => {
      if (result.isConfirmed) {
        handleSave();
      } else {
        dispatch(closeEditCard(deckId, () => navigate(`/card-decks/${deckId}/cards`)));
      }
    });
  };

  const eyebrow = isEdit ? 'Editing card' : 'New card';
  const title = isEdit ? (card?.title || `Card #${id}`) : 'Untitled card';

  return (
    <div className="bg-bg min-h-full">
      <LoadingBackdrop open={isLoading || saveLoading} />
      <div
        className="sticky top-0 bg-bg border-b border-sand"
        style={{ zIndex: 20 }}
      >
        <div
          className="flex items-start"
          style={{ padding: '20px 28px', gap: 16, flexWrap: 'wrap' }}
        >
          <div className="flex-1 min-w-0">
            <div
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 11, letterSpacing: '0.08em' }}
            >
              {eyebrow}
            </div>
            <h1
              className={cn(
                'text-navy truncate',
                !card?.title && isEdit && 'text-text-light italic',
              )}
              style={{
                fontSize: 28,
                fontWeight: 700,
                margin: '4px 0 4px',
                letterSpacing: '-0.015em',
                lineHeight: 1.15,
              }}
              title={title}
            >
              {title}
            </h1>
            <div className="font-mono text-text-muted" style={{ fontSize: 12 }}>
              deck_id · <strong className="text-navy">{deckId}</strong>
              {isEdit && (
                <>
                  {' · '}
                  card_id · <strong className="text-navy">{id}</strong>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center" style={{ gap: 8 }}>
            <Button variant="ghost" onClick={handleCancel} type="button">
              Discard
            </Button>
            <Button
              variant="coral"
              type="submit"
              form="card-form"
              leadingIcon={SaveIconSvg}
            >
              {isEdit ? 'Save' : 'Create'}
            </Button>
          </div>
        </div>
      </div>

      <div style={{ padding: '24px 32px 60px' }}>
        <form
          id="card-form"
          onSubmit={handleSave}
          autoComplete="off"
          style={{ minWidth: 0, maxWidth: 1100 }}
        >
          <CardFormFieldsV2 card={card} handleChange={handleChange} />
        </form>
      </div>
    </div>
  );
}
